import {useAppContext} from "./useAppContext";
import {useDimensions} from "../redux/stateHooks";
import {useProductDetailsQuery} from "./useProductDetailsQuery";
import {contentService} from "../api/contentService";
import {Nullable, PriceType, StockInformationType} from "../definitions/types";
import {PricingModel} from "../models/view/pricingModel";
import {useSuspenseQuery} from "@apollo/client";

const defaultModel = new PricingModel([] as PriceType[], null as Nullable<StockInformationType>);

export const usePricingQuery = () => {
    const {productId, currencyCode} = useAppContext();
    const {width, height} = useDimensions();
    const {
        model: {
            dynamicDimensions: {isDynamic, minimumWidth, minimumHeight},
            stockInformation
        }
    } = useProductDetailsQuery();

    const {error, data} = useSuspenseQuery(contentService.product.prices, {
        errorPolicy: "all",
        pollInterval: 300000,
        variables: {
            currencyCode: currencyCode,
            height: isDynamic ? width && height ? height : minimumHeight : undefined,
            productId: productId,
            width: isDynamic ? width && height ? width : minimumWidth : undefined
        }
    });

    // If there is an error, break early and return the error
    if (error) return {model: defaultModel, currencyCode, error: error.message};

    const rawPrices = data?.product?.prices ?? [] as Nullable<PriceType>[];

    // If there are no prices, break early and return the error
    if (rawPrices.length < 1) return {model: defaultModel, currencyCode, error: 'No prices returned'};

    // Filter out any null prices (which are errors)
    const prices = rawPrices.filter((price): price is PriceType => price !== null);

    // If the number of prices returned does not match the number of prices rendered (due to errors), do not render anything, to avoid the table jumping around
    if (rawPrices.length !== prices.length)
        return {
            currencyCode,
            error: 'At least one price returned was null',
            model: defaultModel
        };

    // Create the pricing model from the prices returned from the API
    const model = new PricingModel(prices, stockInformation);

    // Return the model to the caller
    return {
        currencyCode,
        model
    };
}